import React, { useEffect, useState } from 'react';
import { Sidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';

// Theme MUI
import { Box, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';

// Assets
import sidebarToggleIcon from '../../../assets/images/sidebar-icons/Sidebar-toggle.svg';
import sidebarToggleExpandIcon from '../../../assets/images/sidebar-icons/Sidebar-toggle-expand.svg';
import { sidebarIcons, SidebarIconKey } from '../../../assets/icons';

// Services
import AuthService from '../../../services/auth.service';
import { UserProfile } from '../../../types/UserProfile';

// Constants
import SideMenus from '../../../data/sidemenu.static';

// Components
import AppIcon from '../../common/AppIcon';
import './SideBar.scss';

import StroopwaflLogo from '../../../assets/images/stroopwafl-logo.svg';
import UnilodeLogo from '../../../assets/images/unilode-logo.svg';
import EuldWebPortalLogo from '../../../assets/images/web-portal-logo.svg';

const PREFIX = 'SideBar';

const classes = {
  menuToggle: `${PREFIX}-menuToggle`,
};

const externalUserMenuCategories = ['dashboard', 'visits', 'stockDetails'];
const externalOnlyMenuCategories = ['visits'];

const Root = styled('div')(() => ({
  [`& .${classes.menuToggle}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.3s ease, background-color 0.3s ease',
    '& *': {
      transition: 'color 0.3s ease',
    },
    '&:hover': {
      backgroundColor: '#f0f0f0',
      '& *, & svg': {
        color: '#888 !important',
      },
    },
  },
}));

const CustomLink = styled(Link)`
  text-decoration: none;
  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
  }
`;

// Normalise a path: strip a trailing slash (except for the root "/")
const normalizePath = (p: string): string => (p !== '/' && p.endsWith('/') ? p.replace(/\/+$/, '') : p);

const toIconSlug = (title?: string): string =>
  (title ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const SideBar = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [isCollapsed, setisCollapsed] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => AuthService.getStoredUserProfile());

  useEffect(() => {
    if (isMobile) setisCollapsed(true);
  }, [isMobile]);

  useEffect(() => {
    AuthService.isAuthenticated().then(setIsAuthenticated);
    setUserProfile(AuthService.getStoredUserProfile());
  }, []);

  const isExternalUser = userProfile?.isInternal === false;

  const userMenus = {};
  const hasAccess = (p?: unknown) => {
    void p;
    return true;
  };

  // Does a single url match the current route?
  const matchesPath = (url?: string): boolean => {
    if (!url) return false;
    const path = normalizePath(location.pathname);
    const target = normalizePath(url);
    // Root must match exactly so it doesn't light up on every route
    if (target === '/') return path === '/';
    return path === target || path.startsWith(`${target}/`);
  };

  const isItemActive = (menu: any): boolean => {
    const targets: string[] = [menu?.url, ...(menu?.activeWhen ?? [])];
    return targets.some((t) => matchesPath(t));
  };

  const isMenuActive = (menu: any): boolean => {
    if (isItemActive(menu)) return true;
    if (menu?.subMenus?.length) return menu.subMenus.some((s: any) => isMenuActive(s));
    return false;
  };

  const renderMenuIcon = (menu: any) => {
    const slug = toIconSlug(menu?.title) as SidebarIconKey;
    const pair = sidebarIcons[slug];

    if (pair) {
      return <img src={isMenuActive(menu) ? pair.active : pair.base} alt='' aria-hidden='true' className='sidebar-menu-icon' draggable={false} />;
    }

    return menu?.icon ? <AppIcon name={menu.icon} /> : undefined;
  };

  const createUserMenus = (menus) => {
    Object.keys(menus).map((key) => {
      const _subMenus = menus[key].subMenus;
      if (isExternalUser && !externalUserMenuCategories.includes(menus[key].category)) return key;
      if (!isExternalUser && externalOnlyMenuCategories.includes(menus[key].category)) return key;

      userMenus[menus[key].category] = menus[key];

      if (menus[key].subMenus && menus[key].subMenus.length > 0) {
        // set subMenus to empty array (to be append in createUserSubMenus)
        userMenus[menus[key].category].subMenus = [];
        createUserSubMenus(userMenus[menus[key].category], _subMenus);
      }

      return key;
    });
    return userMenus;
  };

  const createUserSubMenus = (menu, subMenus) => {
    subMenus.forEach((sub: any) => {
      const _subMenus = sub.subMenus;
      if (sub.subMenus.length > 0) {
        const _sub = sub;
        // set subMenus to empty array (to be append in createUserSubMenus)
        _sub.subMenus = [];
        if (hasAccess(sub)) {
          menu.subMenus.push(_sub);
          createUserSubMenus(_sub, _subMenus);
        }
      } else {
        if (hasAccess(sub)) {
          menu.subMenus.push(sub);
        }
      }
    });
  };

  const createSubMenu = (menu, key) => {
    const subMenusToRender = menu.subMenus.filter((x) => !x.guardedPermission || AuthService.hasPermission(x.guardedPermission));

    if (subMenusToRender.length <= 0) return null;

    return !menu.guardedPermission || AuthService.hasPermission(menu.guardedPermission) ? (
      <SubMenu key={key} icon={renderMenuIcon(menu)} label={menu.title} className='sub-menu' defaultOpen={isMenuActive(menu)}>
        {subMenusToRender.map((sub) =>
          sub.subMenus.length > 0 ? (
            createSubMenu(sub, sub.key)
          ) : !sub.guardedPermission || AuthService.hasPermission(sub.guardedPermission) ? (
            <MenuItem
              key={sub.key}
              active={isItemActive(sub)}
              component={<CustomLink key={sub.key} to={sub.url} />}
              icon={renderMenuIcon(sub)}
              className='ps-menu-items-sub'
              title={sub.title}
            >
              <span className='icon'>{sub.title}</span>
            </MenuItem>
          ) : null,
        )}
      </SubMenu>
    ) : null;
  };

  const menus = createUserMenus(SideMenus);

  return (
    <Root className='sidebar-root'>
      <>
        {isAuthenticated && (
          // https://github.com/azouaoui-med/react-pro-sidebar
          <Sidebar
            collapsed={isMobile || isCollapsed}
            toggled={toggled}
            onBackdropClick={() => setToggled(false)}
            backgroundColor='#111827'
            breakPoint='md'
            width='210px'
            className='sidebar-container'
          >
            <div className='sidebar-inner'>
              <div className='sidebar-menu-wrap'>
                <Menu>
                  <MenuItem
                    className={`${classes.menuToggle} sidebar-menu-toggle`}
                    icon={
                      isCollapsed ? (
                        <button
                          type='button'
                          className='sidebar-collapse-icon-button'
                          onClick={() => setisCollapsed((prev) => !prev)}
                          aria-label='Expand sidebar'
                        >
                          <img src={sidebarToggleExpandIcon} alt='Expand sidebar' className='sidebar-toggle-icon' />
                        </button>
                      ) : undefined
                    }
                  >
                    {!isCollapsed && (
                      <Box className='sidebar-brand-stack'>
                        <Box className='sidebar-brand-row'>
                          {isExternalUser ? (
                            <Box className='sidebar-external-brand'>
                              <img src={UnilodeLogo} alt='Unilode' className='sidebar-unilode-logo' draggable={false} />
                              <img src={EuldWebPortalLogo} alt='E-ULD Web Portal' className='sidebar-euld-web-portal-logo' draggable={false} />
                            </Box>
                          ) : (
                            <img src={StroopwaflLogo} alt='Stroopwafl' className='sidebar-brand-logo' draggable={false} />
                          )}

                          <IconButton
                            onClick={() => setisCollapsed((prev) => !prev)}
                            size='large'
                            className='sidebar-collapse-button'
                            aria-label='Collapse sidebar'
                          >
                            <img src={sidebarToggleIcon} alt='Collapse sidebar' className='sidebar-toggle-icon' />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </MenuItem>

                  {Object.keys(menus).map((key) =>
                    menus[key] && menus[key].subMenus && menus[key].subMenus.length > 0 ? (
                      createSubMenu(menus[key], key)
                    ) : !menus[key].guardedPermission || AuthService.hasPermission(menus[key].guardedPermission) ? (
                      <MenuItem
                        key={'menu_' + key}
                        active={isItemActive(menus[key])}
                        component={<CustomLink key={key} to={menus[key].url} />}
                        icon={renderMenuIcon(menus[key])}
                        className='ps-menu-items'
                        title={menus[key].title}
                      >
                        <span className='icon'>{menus[key].title}</span>
                      </MenuItem>
                    ) : null,
                  )}
                </Menu>
              </div>
            </div>
          </Sidebar>
        )}
      </>
    </Root>
  );
};

export default SideBar;
